import type { Booking, Warning } from "../lib/types";
import { evaluateWarnings } from "../lib/warnings";
import type { BoardStore } from "../stores/board";

interface UIStoreLike {
  bookingMode: "view" | "new" | null;
  editing: Booking | null;
  prefillStart: string | null;
  prefillEnd: string | null;
  prefillAircraftId: string | null;
  prefillAircraftSet: boolean;
  close(): void;
  showUndo(b: Booking, message: string): void;
}

function getStores() {
  const a = (window as unknown as { Alpine: { store(name: string): unknown } }).Alpine;
  return {
    board: a.store("board") as BoardStore,
    ui: a.store("ui") as UIStoreLike,
  };
}

function uuid(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return "b-" + Math.random().toString(36).slice(2, 10);
}

export function bookingModalAlpine() {
  return {
    init(this: { b: Booking } & Record<string, unknown>) {
      const { ui, board } = getStores();
      if (ui.editing) {
        this.b = { ...ui.editing };
      } else {
        const firstLesson = board.lookups.lessons[0]?.id ?? "";
        const firstStudent = board.lookups.profiles.find((p) => p.role === "Student")?.id ?? null;
        const firstInstructor =
          board.lookups.profiles.find((p) => p.role === "Flight Instructor")?.id ?? null;
        const firstAircraft = board.lookups.aircraft[0]?.id ?? null;
        this.b = {
          id: uuid(),
          lessonId: firstLesson,
          studentId: firstStudent,
          instructorId: firstInstructor,
          aircraftId: ui.prefillAircraftSet ? ui.prefillAircraftId : firstAircraft,
          start: ui.prefillStart ?? "13:30",
          end: ui.prefillEnd ?? "15:00",
          status: "scheduled",
          ready: false,
          notes: "",
        };
      }
      if (this.b.aircraftId === null) (this.b as unknown as Record<string, string>).aircraftId = "";
      if (this.b.studentId === null) (this.b as unknown as Record<string, string>).studentId = "";
      if (this.b.instructorId === null) (this.b as unknown as Record<string, string>).instructorId = "";
    },
    b: {} as Booking,
    confirmingCancel: false,
    cancelTimer: null as ReturnType<typeof setTimeout> | null,
    get isView(): boolean {
      return getStores().ui.bookingMode === "view";
    },
    get warnings(): Warning[] {
      const { board } = getStores();
      return evaluateWarnings(this.b, board.bookings, {
        aircraft: board.lookups.aircraft,
        certs: board.lookups.certs,
        availability: board.lookups.availability,
        profiles: board.lookups.profiles,
      });
    },
    get blockers(): Warning[] {
      return this.warnings.filter((w) => w.severity === "block");
    },
    get softWarns(): Warning[] {
      return this.warnings.filter((w) => w.severity !== "block");
    },
    async save() {
      if (this.blockers.length > 0) return;
      const { board, ui } = getStores();
      const next: Booking = {
        ...this.b,
        aircraftId: this.b.aircraftId || null,
        studentId: this.b.studentId || null,
        instructorId: this.b.instructorId || null,
      };
      await board.upsertBooking(next);
      ui.close();
    },
    requestCancel(this: { confirmingCancel: boolean; cancelTimer: ReturnType<typeof setTimeout> | null; b: Booking; cancelBooking: () => Promise<void> }) {
      if (this.confirmingCancel) {
        if (this.cancelTimer) { clearTimeout(this.cancelTimer); this.cancelTimer = null; }
        return this.cancelBooking();
      }
      this.confirmingCancel = true;
      this.cancelTimer = setTimeout(() => {
        this.confirmingCancel = false;
        this.cancelTimer = null;
      }, 3000);
    },
    async cancelBooking() {
      const { board, ui } = getStores();
      const previous: Booking = { ...this.b };
      await board.cancelBooking(this.b.id);
      ui.showUndo(previous, "Booking canceled.");
      ui.close();
    },
  };
}
